/** Portfolio dashboard: every plant with its KPIs and its data freshness (§4.1). */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { FreshnessBadge } from '../components/FreshnessBadge';
import { KpiCard } from '../components/KpiCard';
import { Notice } from '../components/Notice';
import { formatCapacity, formatDateTime, formatEnergy, formatNumber, formatPercent, formatPower } from '../lib/format';
import { useApi, useInterval } from '../lib/useApi';

export function PortfolioPage() {
  const { t } = useTranslation();
  const { data, loading, errorCode, errorParams, reload } = useApi(() => api.portfolio(), []);
  // Live-ish refresh. The WebBox bus cycle is minutes, so polling faster would
  // only redraw the same numbers (§5).
  useInterval(reload, 60_000);

  if (loading && !data) return <p className="muted">{t('app.loading')}</p>;
  if (errorCode) return <Notice tone="error" code={errorCode} params={errorParams} />;
  if (!data) return null;

  const { totals, plants } = data;

  return (
    <>
      <h1>{t('nav.portfolio')}</h1>

      <div className="kpi-grid">
        <KpiCard
          label={t('kpi.currentPower')}
          value={formatPower(totals.current_power_w)}
          hint={`${totals.plants_with_fresh_data}/${totals.plant_count} ${t('kpi.reporting')}`}
          tone={totals.plants_with_fresh_data < totals.plant_count ? 'warning' : 'default'}
        />
        <KpiCard label={t('kpi.energyToday')} value={formatEnergy(totals.energy_today_wh)} />
        <KpiCard label={t('kpi.dcCapacity')} value={formatCapacity(totals.dc_capacity_wp)} />
        <KpiCard
          label={t('kpi.openIncidents')}
          value={formatNumber(totals.open_incidents, 0)}
          tone={totals.open_incidents > 0 ? 'critical' : 'default'}
        />
      </div>

      <div className="table-wrap">
        <table>
          <caption className="sr-only">{t('nav.plants')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('nav.plants')}</th>
              <th scope="col">{t('device.status')}</th>
              <th scope="col" className="num">{t('kpi.currentPower')}</th>
              <th scope="col" className="num">{t('kpi.energyToday')}</th>
              <th scope="col" className="num">{t('kpi.specificYield')}</th>
              <th scope="col" className="num">{t('kpi.irradiance')}</th>
              <th scope="col" className="num">{t('kpi.devices')}</th>
              <th scope="col" className="num">{t('kpi.openIncidents')}</th>
              <th scope="col">{t('device.sampleTime')}</th>
            </tr>
          </thead>
          <tbody>
            {plants.map((plant) => {
              const incidents = Object.values(plant.open_incidents).reduce((a, b) => a + b, 0);
              return (
                <tr key={plant.plant_id}>
                  <th scope="row">
                    <Link to={`/plants/${plant.plant_id}`}>{plant.plant_name}</Link>
                    <span className="muted"> · {plant.plant_code}</span>
                  </th>
                  <td>
                    <FreshnessBadge freshness={plant.power_freshness} />
                  </td>
                  <td className="num">{formatPower(plant.current_power_w)}</td>
                  <td className="num">{formatEnergy(plant.energy_today_wh)}</td>
                  <td className="num">
                    {plant.specific_yield_today_wh_per_wp === null
                      ? '—'
                      : `${formatNumber(plant.specific_yield_today_wh_per_wp, 2)} Wh/Wp`}
                  </td>
                  <td className="num">
                    {plant.irradiance_available
                      ? `${formatNumber(plant.irradiance_wm2, 0)} W/m²`
                      : '—'}
                  </td>
                  <td className="num">
                    {plant.devices.online}/{plant.devices.total}
                  </td>
                  <td className="num">{incidents > 0 ? formatNumber(incidents, 0) : '—'}</td>
                  <td>{formatDateTime(plant.newest_sample_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {plants.some((plant) => !plant.irradiance_available) ? (
        <Notice tone="info">{t('plant.noIrradiance')}</Notice>
      ) : null}
      {plants.some((plant) => plant.energy_today_coverage !== null && plant.energy_today_coverage < 0.95) ? (
        <Notice tone="warning">
          {t('report.disclaimers.partial_coverage')}{' '}
          {plants
            .filter((p) => p.energy_today_coverage !== null && p.energy_today_coverage < 0.95)
            .map((p) => `${p.plant_code}: ${formatPercent(p.energy_today_coverage)}`)
            .join(', ')}
        </Notice>
      ) : null}
    </>
  );
}
